import { useLocation } from "@reach/router";
import { Link } from "gatsby";
import { Fragment } from "react";

import { Flex, NavLink, StrapiLink, Text } from "@theme/components";

import type { ThemeUIStyleObject } from "@theme/components";
import type { NavigationDataProps } from "@theme/types";

interface NavMobileDropdownProps {
  navData: NavigationDataProps;
  NavLinkTextStyles: ThemeUIStyleObject;
  selectedDropdownIndex: number;
  closeDrawer: () => void;
}

// Note this component is shadowed to add support for CLP search params. All updates must be done in both gatsby-theme and gatsby-site
export const NavMobileDropdown = ({
  navData,
  NavLinkTextStyles,
  selectedDropdownIndex,
  closeDrawer,
}: NavMobileDropdownProps) => {
  const { search } = useLocation();
  const { dropdownLinks, languages, currentLanguage, originalPath } = navData;
  const isLanguageDropdown = selectedDropdownIndex === dropdownLinks.length;
  const selectedDropdown = isLanguageDropdown ? null : dropdownLinks[selectedDropdownIndex];

  return (
    <Flex
      sx={{
        flexDirection: "column",
        gap: "s400",
        alignSelf: "stretch",
      }}
    >
      {isLanguageDropdown
        ? languages.map((language) => (
            <Link
              key={language}
              to={`${language !== "en" ? `/${language}` : ""}${originalPath}${search || ""}`}
              onClick={closeDrawer}
              sx={{
                variant: "links.default",
                position: "relative",
                pointerEvents: language === currentLanguage ? "none" : "auto",
                textDecoration: "none",
              }}
            >
              <Text
                variant="sansBody03Regular"
                sx={{
                  position: "relative",
                  textTransform: "uppercase",
                  ...NavLinkTextStyles,
                }}
              >
                {language.toUpperCase()}
              </Text>
            </Link>
          ))
        : selectedDropdown?.links.map((link, linkIndex) => (
            <Fragment key={`${link.label}-${linkIndex}`}>
              {"to" in link && link.to ? (
                <NavLink
                  aria-label={link.label}
                  sx={{ ...NavLinkTextStyles }}
                  to={link.to as string}
                  onClick={closeDrawer}
                >
                  {link.label}
                </NavLink>
              ) : (
                <StrapiLink
                  sx={{ ...NavLinkTextStyles }}
                  aria-label={link.label}
                  onClick={closeDrawer}
                  {...link}
                />
              )}
            </Fragment>
          ))}
    </Flex>
  );
};
